/**
 * Which of our numbers a text leaves from (ADR 0310).
 *
 * Jonathon, 2026-10-24: "there needs to be a drop down so we can see and
 * choose which number we're texting from. defaulting to our own of course."
 *
 * A line is a Twilio number a person's profile names (`roof_board_line`):
 * Jonathon's, Lysa's and Samuel's. The list is built from the staff the
 * session already holds, the rep's own line first and chosen, the others in
 * the order the staff are listed. A person with no line of their own still
 * gets the list and starts on the first of them, which is the Roof Board
 * line while Jonathon's profile sorts first.
 */

import { formatPhone } from "./phone.js";
import { toE164 } from "./partners.js";

export interface LineOwner {
  id: string;
  full_name: string | null;
  roof_board_line?: string | null;
  active?: boolean | null;
  sort_order?: number | null;
}

export interface TextLine {
  /** E.164, what `twilio-send` is told. */
  number: string;
  /** "Lysa's line · 205-946-4662". */
  label: string;
  /** The profile it belongs to. */
  owner_id: string;
  mine: boolean;
}

/** The possessive of a first name: "Jonathon's", "Lysa's", "Chris'". */
function possessive(fullName: string | null): string {
  const first = (fullName ?? "").trim().split(/\s+/)[0] ?? "";
  if (first === "") return "Their";
  return first.endsWith("s") ? `${first}'` : `${first}'s`;
}

/**
 * The lines a rep may text from, own line first.
 *
 * One entry per number: two profiles naming the same line would be a data
 * mistake, and the first wins. Inactive staff keep their line in the list
 * only while it is the caller's own, which cannot happen.
 */
export function textLines(
  people: readonly LineOwner[],
  myProfileId: string | null | undefined,
): TextLine[] {
  const seen = new Set<string>();
  const lines: TextLine[] = [];
  const ordered = [...people].sort(
    (a, b) =>
      (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
      (b.sort_order ?? Number.MAX_SAFE_INTEGER),
  );
  for (const person of ordered) {
    if (person.active === false) continue;
    const number = toE164(person.roof_board_line);
    if (!number || seen.has(number)) continue;
    seen.add(number);
    lines.push({
      number,
      label: `${possessive(person.full_name)} line · ${formatPhone(number.replace(/^\+1/, "")) ?? number}`,
      owner_id: person.id,
      mine: person.id === myProfileId,
    });
  }
  lines.sort((a, b) => Number(b.mine) - Number(a.mine));
  return lines;
}

/** The line a rep starts on: their own, else the first there is, else none. */
export function defaultTextLine(lines: readonly TextLine[]): string | null {
  return lines.find((l) => l.mine)?.number ?? lines[0]?.number ?? null;
}
